import { useMemo, useRef, useState, type InputHTMLAttributes } from 'react';
import {
  completeMenglianMultipartUpload,
  initiateMenglianMultipartUploads,
  signMenglianMultipartParts,
  stageMenglianDataUploads,
  uploadMenglianFile,
  uploadMenglianPart,
  type MenglianMultipartCompletedPart,
  type MenglianStagedUpload,
} from '../lib/menglian-data-upload-api';
import './MenglianDataUploadPage.css';

type UploadStatus = 'ready' | 'staging' | 'uploading' | 'done' | 'error';
type UploadMode = 'files' | 'folder';

interface UploadItem {
  id: string;
  file: File;
  relativePath: string;
  progress: number;
  status: UploadStatus;
  error?: string;
  storageUri?: string;
}

interface ResumePart {
  partNumber: number;
  etag: string;
  sizeBytes: number;
}

interface ResumeRecord {
  version: 1;
  batchId: string;
  relativePath: string;
  sizeBytes: number;
  lastModified: number;
  storageUri: string;
  uploadId: string;
  partSizeBytes: number;
  totalParts: number;
  parts: ResumePart[];
  updatedAt: number;
}

type DirectoryInputProps = InputHTMLAttributes<HTMLInputElement> & {
  webkitdirectory?: string;
  directory?: string;
};

const MAX_FILE_SIZE_BYTES = 1024 * 1024 * 1024;
const PART_CONCURRENT_UPLOADS = 3;
const RESUME_RECORD_VERSION = 1;

function getRelativePath(file: File): string {
  const maybeFolderFile = file as File & { webkitRelativePath?: string };
  return maybeFolderFile.webkitRelativePath || file.name;
}

function fileId(file: File, relativePath: string): string {
  return `${relativePath}-${file.size}-${file.lastModified}`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function createItems(files: File[]): UploadItem[] {
  return files.map((file) => {
    const relativePath = getRelativePath(file).replaceAll('\\', '/');
    const tooLarge = file.size > MAX_FILE_SIZE_BYTES;
    return {
      id: fileId(file, relativePath),
      file,
      relativePath,
      progress: 0,
      status: tooLarge ? 'error' : 'ready',
      error: tooLarge ? '单文件不能超过 1 GB' : undefined,
    };
  });
}

function isUploadable(item: UploadItem): boolean {
  return item.file.size <= MAX_FILE_SIZE_BYTES && (item.status === 'ready' || item.status === 'error');
}

function resumeStorageKey(item: UploadItem): string {
  return `menglian-data-upload:${item.id}`;
}

function loadResumeRecord(item: UploadItem): ResumeRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(resumeStorageKey(item));
    if (!raw) return null;
    const record = JSON.parse(raw) as ResumeRecord;
    if (
      record.version !== RESUME_RECORD_VERSION ||
      record.relativePath !== item.relativePath ||
      record.sizeBytes !== item.file.size ||
      record.lastModified !== item.file.lastModified ||
      !record.storageUri ||
      !record.uploadId ||
      record.partSizeBytes <= 0 ||
      record.totalParts <= 0 ||
      !Array.isArray(record.parts)
    ) {
      window.localStorage.removeItem(resumeStorageKey(item));
      return null;
    }
    return record;
  } catch {
    return null;
  }
}

function saveResumeRecord(item: UploadItem, record: ResumeRecord): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      resumeStorageKey(item),
      JSON.stringify({ ...record, updatedAt: Date.now() }),
    );
  } catch {
    // localStorage is only a convenience resume cache; uploads still continue without it.
  }
}

function removeResumeRecord(item: UploadItem): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(resumeStorageKey(item));
  } catch {
    // Ignore storage cleanup failures.
  }
}

function partRange(file: File, partNumber: number, partSizeBytes: number): { start: number; end: number } {
  const start = (partNumber - 1) * partSizeBytes;
  const end = Math.min(file.size, start + partSizeBytes);
  return { start, end };
}

export default function MenglianDataUploadPage() {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const [items, setItems] = useState<UploadItem[]>([]);
  const [uploadMode, setUploadMode] = useState<UploadMode>('files');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [batchId, setBatchId] = useState<string>('');
  const [batchError, setBatchError] = useState<string>('');

  const summary = useMemo(() => {
    const totalBytes = items.reduce((sum, item) => sum + item.file.size, 0);
    const doneCount = items.filter((item) => item.status === 'done').length;
    const failedCount = items.filter((item) => item.status === 'error').length;
    return { totalBytes, doneCount, failedCount };
  }, [items]);

  const uploadableCount = useMemo(() => items.filter(isUploadable).length, [items]);

  const mergeFiles = (files: File[]) => {
    if (files.length === 0) return;
    setBatchError('');
    setItems((current) => {
      const byId = new Map(current.map((item) => [item.id, item]));
      createItems(files).forEach((item) => byId.set(item.id, item));
      return Array.from(byId.values()).sort((a, b) => a.relativePath.localeCompare(b.relativePath));
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    mergeFiles(Array.from(event.target.files ?? []));
    event.target.value = '';
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    mergeFiles(Array.from(event.dataTransfer.files));
  };

  const updateItem = (id: string, patch: Partial<UploadItem>) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  };

  const prepareMultipartRecords = async (uploadItems: UploadItem[]): Promise<Map<string, ResumeRecord>> => {
    const records = new Map<string, ResumeRecord>();
    const needsInitiate: UploadItem[] = [];

    uploadItems.forEach((item) => {
      const existing = loadResumeRecord(item);
      if (existing) {
        records.set(item.id, existing);
        setBatchId(existing.batchId);
        return;
      }
      needsInitiate.push(item);
    });

    if (needsInitiate.length === 0) return records;

    const initiated = await initiateMenglianMultipartUploads(
      needsInitiate.map((item) => ({
        relative_path: item.relativePath,
        size_bytes: item.file.size,
        content_type: item.file.type || 'application/octet-stream',
      })),
    );
    setBatchId(initiated.batch_id);

    const uploadByPath = new Map(initiated.uploads.map((upload) => [upload.relative_path, upload]));
    needsInitiate.forEach((item) => {
      const upload = uploadByPath.get(item.relativePath);
      if (!upload) return;
      const record: ResumeRecord = {
        version: RESUME_RECORD_VERSION,
        batchId: initiated.batch_id,
        relativePath: item.relativePath,
        sizeBytes: item.file.size,
        lastModified: item.file.lastModified,
        storageUri: upload.storage_uri,
        uploadId: upload.upload_id,
        partSizeBytes: upload.part_size_bytes,
        totalParts: upload.total_parts,
        parts: [],
        updatedAt: Date.now(),
      };
      records.set(item.id, record);
      saveResumeRecord(item, record);
    });

    return records;
  };

  const signPart = async (record: ResumeRecord, partNumber: number) => {
    const signed = await signMenglianMultipartParts({
      storage_uri: record.storageUri,
      upload_id: record.uploadId,
      part_numbers: [partNumber],
    });
    const part = signed.parts.find((candidate) => candidate.part_number === partNumber);
    if (!part) throw new Error(`没有获取到第 ${partNumber} 个分片的上传地址`);
    return part;
  };

  const uploadPartWithFreshSignature = async (
    record: ResumeRecord,
    partNumber: number,
    blob: Blob,
  ): Promise<{ etag: string }> => {
    const firstSignedPart = await signPart(record, partNumber);
    try {
      return await uploadMenglianPart(blob, firstSignedPart);
    } catch {
      const renewedSignedPart = await signPart(record, partNumber);
      return uploadMenglianPart(blob, renewedSignedPart);
    }
  };

  const uploadMultipartItem = async (item: UploadItem, record: ResumeRecord) => {
    updateItem(item.id, {
      status: 'uploading',
      progress: Math.max(1, Math.floor((record.parts.length / record.totalParts) * 100)),
      storageUri: record.storageUri,
      error: undefined,
    });

    const completedParts = new Map<number, ResumePart>(
      record.parts.map((part) => [part.partNumber, part]),
    );
    let nextPartNumber = 1;

    const completedBytes = () =>
      Array.from(completedParts.values()).reduce((sum, part) => sum + part.sizeBytes, 0);

    const persistProgress = () => {
      record.parts = Array.from(completedParts.values()).sort(
        (a, b) => a.partNumber - b.partNumber,
      );
      saveResumeRecord(item, record);
      const progress = item.file.size > 0 ? Math.floor((completedBytes() / item.file.size) * 100) : 100;
      updateItem(item.id, { progress: Math.min(99, Math.max(1, progress)) });
    };

    const worker = async () => {
      for (;;) {
        const partNumber = nextPartNumber;
        nextPartNumber += 1;
        if (partNumber > record.totalParts) return;
        if (completedParts.has(partNumber)) continue;

        const { start, end } = partRange(item.file, partNumber, record.partSizeBytes);
        const blob = item.file.slice(start, end);
        const result = await uploadPartWithFreshSignature(record, partNumber, blob);
        completedParts.set(partNumber, {
          partNumber,
          etag: result.etag,
          sizeBytes: end - start,
        });
        persistProgress();
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(PART_CONCURRENT_UPLOADS, record.totalParts) }, () => worker()),
    );

    const parts: MenglianMultipartCompletedPart[] = Array.from(completedParts.values())
      .sort((a, b) => a.partNumber - b.partNumber)
      .map((part) => ({ part_number: part.partNumber, etag: part.etag }));

    if (parts.length !== record.totalParts) {
      throw new Error('分片没有全部上传完成，已保留进度，可重新点击继续上传');
    }

    await completeMenglianMultipartUpload({
      storage_uri: record.storageUri,
      upload_id: record.uploadId,
      parts,
    });
    removeResumeRecord(item);
    updateItem(item.id, { status: 'done', progress: 100, storageUri: record.storageUri });
  };

  const uploadDirectItems = async (directItems: UploadItem[]) => {
    const staged = await stageMenglianDataUploads(
      directItems.map((item) => ({
        relative_path: item.relativePath,
        size_bytes: item.file.size,
        content_type: item.file.type || 'application/octet-stream',
      })),
    );
    setBatchId(staged.batch_id);
    const stagedByPath = new Map(staged.uploads.map((upload) => [upload.relative_path, upload]));

    for (const item of directItems) {
      const upload: MenglianStagedUpload | undefined = stagedByPath.get(item.relativePath);
      if (!upload) {
        updateItem(item.id, { status: 'error', error: '未获取到上传地址' });
        continue;
      }
      updateItem(item.id, {
        status: 'uploading',
        progress: 1,
        storageUri: upload.storage_uri,
        error: undefined,
      });
      try {
        await uploadMenglianFile(item.file, upload, (progress) => updateItem(item.id, { progress }));
        updateItem(item.id, { status: 'done', progress: 100 });
      } catch (error) {
        updateItem(item.id, {
          status: 'error',
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }
  };

  const runUploads = async () => {
    const readyItems = items.filter(isUploadable);
    if (readyItems.length === 0 || isUploading) return;

    setIsUploading(true);
    setBatchError('');
    setItems((current) =>
      current.map((item) =>
        isUploadable(item)
          ? { ...item, status: 'staging', progress: 0, error: undefined, storageUri: undefined }
          : item,
      ),
    );

    try {
      const directItems = readyItems.filter((item) => item.file.size === 0);
      const multipartItems = readyItems.filter((item) => item.file.size > 0);

      if (directItems.length > 0) {
        await uploadDirectItems(directItems);
      }

      if (multipartItems.length > 0) {
        const records = await prepareMultipartRecords(multipartItems);
        for (const item of multipartItems) {
          const record = records.get(item.id);
          if (!record) {
            updateItem(item.id, { status: 'error', error: '未初始化分片上传任务' });
            continue;
          }
          try {
            await uploadMultipartItem(item, record);
          } catch (error) {
            updateItem(item.id, {
              status: 'error',
              error: error instanceof Error ? error.message : String(error),
              storageUri: record.storageUri,
            });
          }
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setBatchError(message);
      setItems((current) =>
        current.map((item) =>
          item.status === 'staging' ? { ...item, status: 'ready', error: undefined } : item,
        ),
      );
    } finally {
      setIsUploading(false);
    }
  };

  const clearFinished = () => {
    setItems((current) => current.filter((item) => item.status !== 'done'));
  };

  const openSelectedPicker = () => {
    if (uploadMode === 'folder') {
      folderInputRef.current?.click();
      return;
    }
    fileInputRef.current?.click();
  };

  return (
    <main className="menglian-data-page">
      <div className="menglian-data-shell">
        <section className="menglian-data-hero">
          <div className="menglian-data-title-card">
            <div className="menglian-data-eyebrow">
              <span />
              MENGLIAN AVOCADO RESEARCH ARCHIVE
            </div>
            <h1>孟连县牛油果科研中心资料上传端口</h1>
            <p className="menglian-data-lede">
              面向科研记录、田间照片、实验表格和项目材料的统一上传入口。文件会直接归档到腾讯云存储的
              “孟连科研中心资料”目录，支持 1 GB 单文件、分片上传、失败续传和签名过期自动刷新。
            </p>
            <div className="menglian-data-stats" aria-label="上传能力说明">
              <div className="menglian-data-stat">
                <strong>1 GB</strong>
                <span>单文件上限</span>
              </div>
              <div className="menglian-data-stat">
                <strong>500</strong>
                <span>单批最多文件</span>
              </div>
            </div>
          </div>

          <div className="menglian-data-upload-card">
            <div className="menglian-data-upload-tabs" role="tablist" aria-label="上传类型">
              <button
                className={`menglian-data-upload-tab${uploadMode === 'files' ? ' active' : ''}`}
                type="button"
                role="tab"
                aria-selected={uploadMode === 'files'}
                onClick={() => setUploadMode('files')}
              >
                上传文件
              </button>
              <button
                className={`menglian-data-upload-tab${uploadMode === 'folder' ? ' active' : ''}`}
                type="button"
                role="tab"
                aria-selected={uploadMode === 'folder'}
                onClick={() => setUploadMode('folder')}
              >
                本地文件夹
              </button>
            </div>
            <div
              className={`menglian-data-dropzone${isDragging ? ' is-dragging' : ''}`}
              role="button"
              tabIndex={0}
              onDragEnter={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={openSelectedPicker}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openSelectedPicker();
                }
              }}
            >
              <div className="menglian-data-upload-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false">
                  <path d="M12 16V5" />
                  <path d="M7.5 9.5 12 5l4.5 4.5" />
                  <path d="M5 15.5v2.25A2.25 2.25 0 0 0 7.25 20h9.5A2.25 2.25 0 0 0 19 17.75V15.5" />
                </svg>
              </div>
              <h2>{uploadMode === 'folder' ? 'Drop folder here' : 'Drop files here'}</h2>
              <p>
                {uploadMode === 'folder'
                  ? 'or click to browse a local folder; subfolders will be preserved'
                  : 'or click to browse your computer'}
              </p>
              <input
                ref={fileInputRef}
                className="menglian-data-hidden-input"
                type="file"
                multiple
                onChange={handleInputChange}
              />
              <input
                ref={folderInputRef}
                className="menglian-data-hidden-input"
                type="file"
                multiple
                onChange={handleInputChange}
                {...({ webkitdirectory: '', directory: '' } satisfies DirectoryInputProps)}
              />
              <div className="menglian-data-note">
                每个分片上传前都会重新获取临时签名；如果网络中断或签名过期，重新点击开始上传即可从已完成分片继续。
                请勿在文件名中使用盘符、上级目录或系统保留字符。
              </div>
            </div>
          </div>
        </section>

        {items.length > 0 && (
          <section className="menglian-data-queue-card" aria-label="上传队列">
            <div className="menglian-data-queue-head">
              <div>
                <h2>待上传资料</h2>
                <p>
                  {items.length} 个文件，合计 {formatBytes(summary.totalBytes)}
                  {batchId ? ` · 批次 ${batchId}` : ''}
                </p>
              </div>
              <div className="menglian-data-actions">
                <button
                  className="menglian-data-button"
                  type="button"
                  onClick={clearFinished}
                  disabled={isUploading || summary.doneCount === 0}
                >
                  清理已完成
                </button>
                <button
                  className="menglian-data-button primary"
                  type="button"
                  onClick={runUploads}
                  disabled={isUploading || uploadableCount === 0}
                >
                  {isUploading ? '上传中' : summary.failedCount > 0 ? '继续上传' : '开始上传'}
                </button>
              </div>
            </div>

            <div className="menglian-data-list">
              {items.map((item) => (
                <div className="menglian-data-file-row" key={item.id}>
                  <div>
                    <div className="menglian-data-file-name" title={item.relativePath}>
                      {item.relativePath}
                    </div>
                    <div className="menglian-data-file-meta">
                      {formatBytes(item.file.size)}
                      {item.storageUri ? ` · ${item.storageUri}` : ''}
                      {item.error ? ` · ${item.error}` : ''}
                    </div>
                  </div>
                  <div className="menglian-data-progress" aria-label={`${item.progress}%`}>
                    <span style={{ width: `${item.progress}%` }} />
                  </div>
                  <div className={`menglian-data-status ${item.status}`}>
                    {item.status === 'ready' && '待上传'}
                    {item.status === 'staging' && '准备中'}
                    {item.status === 'uploading' && `${item.progress}%`}
                    {item.status === 'done' && '已归档'}
                    {item.status === 'error' && '可重试'}
                  </div>
                </div>
              ))}
            </div>

            {batchError && <div className="menglian-data-alert">{batchError}</div>}
            {summary.doneCount > 0 && summary.failedCount === 0 && (
              <div className="menglian-data-success">
                已完成 {summary.doneCount} 个文件上传，资料已进入“孟连科研中心资料”目录。
              </div>
            )}
          </section>
        )}
      </div>
    </main>
  );
}
